import React, { SyntheticEvent, useCallback, useEffect } from 'react';

import styled, { useTheme } from 'styled-components';

import { Notification } from '@cd3p/core/modules/notifications';
import { Alert, AlertTitle } from 'components';
import { ToastContentProps, toast } from 'react-toastify';

import { useSelector } from 'third-party';

import { useApp } from 'modules/app';
import { useNotifications } from 'modules/notifications';
import { useStorage } from 'modules/storage';

import { appSelectors, notificationsSelectors } from 'selectors';

import { isTitleString } from 'utils/app';
import { showPushNotification } from 'utils/pushNotification';

const StyledAlert = styled(Alert)<{
  showpointer?: string;
  background?: string;
}>`
  margin-bottom: 2.5rem;
  max-width: 80rem;
  max-height: 30rem;
  justify-self: center;
  background-color: ${props => (props.background ? props.background : 'unset')};
  box-shadow: 0px 2px 5px 0px rgba(0, 0, 0, 0.3);
  cursor: ${props => (props.showpointer === 'true' ? 'pointer' : 'default')};
  strong {
    font-weight: bold;
  }
  &:last-child {
    margin-bottom: 0;
  }
`;

type AnimatedNotificationProps = {
  notification: Notification;
  onClose: () => void;
  onClick?: (closeNotification: () => void) => void;
};

const NotificationToast: React.FC<
  AnimatedNotificationProps & Partial<ToastContentProps>
> = ({ notification, onClose, onClick, closeToast }) => {
  const { updateUserSettingInStorage } = useStorage();
  const { setSelectedProvider } = useApp();
  const providerId = useSelector(appSelectors.providerId);
  const theme = useTheme();

  const notificationColor = notification.severity
    ? (notification.severity as unknown as string)
    : theme.custom.palette.white;

  const closeBoth = useCallback(() => {
    closeToast?.();
    onClose();
  }, [closeToast, onClose]);

  const closeWithCallback = useCallback(
    (callback: (notification?: Notification) => void) => () => {
      callback(notification);
      closeBoth();
    },
    [closeBoth, notification],
  );

  const handleClick = useCallback(() => {
    if (notification.providerId && providerId != notification.providerId) {
      updateUserSettingInStorage({
        currentProviderId: notification.providerId,
      });
      setSelectedProvider(notification.providerId).then(() =>
        onClick?.(closeBoth),
      );
      return;
    }
    onClick?.(closeBoth);
  }, [
    notification.providerId,
    providerId,
    onClick,
    closeBoth,
    setSelectedProvider,
    updateUserSettingInStorage,
  ]);

  return (
    <StyledAlert
      severity={notification.severity}
      onClose={(event: SyntheticEvent) => {
        event.stopPropagation();
        closeBoth();
      }}
      background={notificationColor}
      onClick={handleClick}
      showpointer={onClick ? 'true' : 'false'}
    >
      <div>
        {isTitleString(notification.title) ? (
          <AlertTitle style={{ fontWeight: 600, textTransform: 'uppercase' }}>
            {notification.title as React.ReactNode}
          </AlertTitle>
        ) : (
          (notification.title as React.ReactNode)
        )}
      </div>
      <div>
        {
          (typeof notification.message === 'function'
            ? notification.message({
                close: closeBoth,
                closeWithCallback,
              })
            : notification.message) as React.ReactNode
        }
      </div>
    </StyledAlert>
  );
};

export const Notifications = () => {
  const { removeNotification } = useNotifications();
  const notificationItems = useSelector(
    notificationsSelectors.notificationItems,
  );

  useEffect(() => {
    notificationItems.forEach(notification => {
      const id = String(notification.id || '');
      if (toast.isActive(id)) return;

      const autoClose =
        notification.autoHide === false ? false : Number(notification.autoHide);

      const render = (
        <NotificationToast
          notification={notification}
          onClose={() => removeNotification(notification.id)}
          onClick={notification.onClick}
        />
      );

      toast(render, {
        toastId: id,
        autoClose,
        closeOnClick: false,
        pauseOnFocusLoss: false,
        pauseOnHover: true,
        position: 'top-center',
        closeButton: false,
        hideProgressBar: true,
        draggable: false,
        style: {
          background: 'transparent',
          boxShadow: 'none',
          padding: 0,
          margin: 0,
          width: 'fit-content',
          maxWidth: '80rem',
        },
        onClose: () => removeNotification(notification.id),
      });

      if (notification.sound && typeof notification.title === 'string') {
        showPushNotification(notification.title, notification.onClick);
      }
    });
  }, [notificationItems, removeNotification]);

  return null;
};
